import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RotateCcw, SkipForward } from 'lucide-react';
import { PREF, readBoolPref, writePref } from './playerPrefs';

type Props = {
  nextTitle?: string;
  onReplay: () => void;
  onNext?: () => void;
};

const AUTO_NEXT_SECONDS = 8;

/**
 * Pantalla de cierre de una lección: repetir, pasar a la siguiente y, si el
 * estudiante lo tiene activado, una cuenta atrás cancelable. La finalización de
 * la lección ya la decidió el servidor; esta tarjeta solo ayuda a continuar.
 */
export default function EndOfVideoCard({ nextTitle, onReplay, onNext }: Props) {
  const [autoNext, setAutoNext] = useState(() => readBoolPref(PREF.autoNext, true));
  const [remaining, setRemaining] = useState(AUTO_NEXT_SECONDS);
  const [cancelled, setCancelled] = useState(false);
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const onNextRef = useRef(onNext);
  useLayoutEffect(() => { onNextRef.current = onNext; }, [onNext]);

  const counting = Boolean(onNext) && autoNext && !cancelled;

  useEffect(() => {
    primaryRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (!counting) return;
    if (remaining <= 0) {
      // Pestaña oculta: nunca navegar a espaldas del estudiante.
      if (document.visibilityState === 'visible') {
        onNextRef.current?.();
      } else {
        setCancelled(true);
      }
      return;
    }
    const id = window.setTimeout(() => setRemaining((value) => value - 1), 1000);
    return () => window.clearTimeout(id);
  }, [counting, remaining]);

  const toggleAutoNext = (value: boolean) => {
    setAutoNext(value);
    writePref(PREF.autoNext, String(value));
    if (!value) setCancelled(true);
  };

  const buttonBase =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition-colors';

  return (
    <div className="motion-fade absolute inset-0 z-30 flex flex-col items-center justify-center gap-5 bg-black/85 px-6 text-center text-white">
      <div role="status" className="space-y-1">
        <p className="text-lg font-semibold">Lección terminada</p>
        {counting && (
          <p className="sr-only">Pasaremos a la siguiente lección en {AUTO_NEXT_SECONDS} segundos. Puedes cancelar.</p>
        )}
        {nextTitle && <p className="max-w-md truncate text-sm text-white/70">Sigue: {nextTitle}</p>}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onReplay}
          ref={onNext ? undefined : primaryRef}
          className={`${buttonBase} ${onNext ? 'border border-white/25 text-white hover:bg-white/10' : 'bg-primary text-primary-foreground hover:brightness-110'}`}
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Ver de nuevo
        </button>
        {onNext && (
          <button
            type="button"
            ref={primaryRef}
            onClick={onNext}
            className={`${buttonBase} bg-primary text-primary-foreground hover:bg-primary/90`}
          >
            <SkipForward className="h-4 w-4" aria-hidden="true" />
            Siguiente{counting ? ` en ${remaining} s` : ''}
          </button>
        )}
      </div>

      {counting && (
        <div className="flex w-full max-w-xs items-center gap-3" aria-hidden="true">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/20">
            <div
              className="h-full origin-left bg-primary transition-transform duration-1000 ease-linear motion-reduce:transition-none"
              style={{ transform: `scaleX(${remaining / AUTO_NEXT_SECONDS})` }}
            />
          </div>
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setCancelled(true)}
            className="text-xs font-semibold text-white/80 underline-offset-2 hover:underline"
          >
            Cancelar
          </button>
        </div>
      )}
      {counting && (
        <button
          type="button"
          onClick={() => setCancelled(true)}
          className="sr-only focus:not-sr-only focus:rounded focus:bg-white/10 focus:px-3 focus:py-2 focus:text-xs focus:text-white"
        >
          Cancelar la reproducción automática
        </button>
      )}

      {onNext && (
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-xs text-white/80">
          <input
            type="checkbox"
            checked={autoNext}
            onChange={(event) => toggleAutoNext(event.target.checked)}
            className="h-4 w-4 accent-[var(--primary)]"
          />
          Reproducir la siguiente automáticamente
        </label>
      )}
    </div>
  );
}
